// frontend-react/src/store/index.js
// Store Zustand principal pour la gestion d'état globale
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export const useStore = create(
  persist(
    (set, get) => ({
      // ============================================
      // AUTH
      // ============================================
      user: null,
      token: null,
      setAuth: (user, token) => set({ user, token }),
      logout: () => set({ user: null, token: null }),
      isAuthenticated: () => !!get().user,

      // ============================================
      // PRODUCTS (cache)
      // ============================================
      products: [],
      productsLoading: false,
      productsLastFetch: null,
      setProducts: (products) => set({
        products,
        productsLastFetch: Date.now()
      }),
      setProductsLoading: (loading) => set({ productsLoading: loading }),

      // Check si le cache est valide (5 minutes)
      isProductsCacheValid: () => {
        const lastFetch = get().productsLastFetch;
        if (!lastFetch) return false;
        return Date.now() - lastFetch < 5 * 60 * 1000;
      },

      // ============================================
      // CART (vente en cours)
      // ============================================
      cart: [],
      addToCart: (item) => set((s) => ({
        cart: [...s.cart, { ...item, cartId: Date.now() + Math.random() }]
      })),
      updateCartItem: (cartId, updates) => set((s) => ({
        cart: s.cart.map(item =>
          item.cartId === cartId ? { ...item, ...updates } : item
        )
      })),
      removeFromCart: (cartId) => set((s) => ({
        cart: s.cart.filter(i => i.cartId !== cartId)
      })),
      clearCart: () => set({ cart: [] }),

      // Calcul du total HT du panier
      getCartTotal: () => {
        return get().cart.reduce((sum, item) =>
          sum + (Number(item.qty) || 0) * (Number(item.unitPrice) || 0), 0
        );
      },

      // ============================================
      // UI STATE
      // ============================================
      sidebarOpen: true,
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      // Theme (dark/light)
      theme: 'light',
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set((s) => ({
        theme: s.theme === 'light' ? 'dark' : 'light'
      })),

      // Lock screen (inactivité)
      locked: false,
      setLocked: (locked) => set({ locked }),
      lock: () => set({ locked: true }),
      unlock: () => set({ locked: false }),

      // ============================================
      // ORDERS (historique local des ventes)
      // ============================================
      orders: [],
      addOrder: (order) => set((s) => ({
        orders: [order, ...s.orders]
      })),
      setOrders: (orders) => set({ orders }),
      clearOrders: () => set({ orders: [] }),

      // ============================================
      // NOTIFICATIONS / TOASTS
      // ============================================
      notifications: [],
      addNotification: (notification) => set((s) => ({
        notifications: [
          ...s.notifications,
          {
            id: Date.now(),
            ...notification,
            createdAt: new Date()
          }
        ]
      })),
      removeNotification: (id) => set((s) => ({
        notifications: s.notifications.filter(n => n.id !== id)
      })),
      clearNotifications: () => set({ notifications: [] }),

      // ============================================
      // FILTERS / SEARCH STATE (persisté par page)
      // ============================================
      filters: {},
      setFilter: (page, filter) => set((s) => ({
        filters: { ...s.filters, [page]: filter }
      })),
      getFilter: (page) => get().filters[page] || {},
      clearFilter: (page) => set((s) => {
        const { [page]: _, ...rest } = s.filters;
        return { filters: rest };
      }),

      // ============================================
      // OFFLINE QUEUE
      // ============================================
      offlineQueue: [],
      addToOfflineQueue: (action) => set((s) => ({
        offlineQueue: [
          ...s.offlineQueue,
          { ...action, id: Date.now() + Math.random(), queuedAt: new Date().toISOString() },
        ],
      })),
      removeFromOfflineQueue: (id) => set((s) => ({
        offlineQueue: s.offlineQueue.filter((a) => a.id !== id),
      })),
      clearOfflineQueue: () => set({ offlineQueue: [] }),
    }),
    {
      name: 'fedis-storage',
      // Persister uniquement certains champs
      partialize: (state) => ({
        user: state.user,
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
        offlineQueue: state.offlineQueue,
      }),
    }
  )
);

// Hook utilitaire pour accéder à l'auth
export const useAuth = () => {
  const user = useStore((s) => s.user);
  const token = useStore((s) => s.token);
  const setAuth = useStore((s) => s.setAuth);
  const logout = useStore((s) => s.logout);

  return { user, token, setAuth, logout, isAuthenticated: !!user };
};

// Hook utilitaire pour accéder aux produits
export const useProducts = () => {
  const products = useStore((s) => s.products);
  const loading = useStore((s) => s.productsLoading);
  const setProducts = useStore((s) => s.setProducts);
  const setLoading = useStore((s) => s.setProductsLoading);
  const isCacheValid = useStore((s) => s.isProductsCacheValid);

  return { products, loading, setProducts, setLoading, isCacheValid };
};

// Hook utilitaire pour le panier
export const useCart = () => {
  const cart = useStore((s) => s.cart);
  const addToCart = useStore((s) => s.addToCart);
  const updateCartItem = useStore((s) => s.updateCartItem);
  const removeFromCart = useStore((s) => s.removeFromCart);
  const clearCart = useStore((s) => s.clearCart);
  const getTotal = useStore((s) => s.getCartTotal);

  return { cart, addToCart, updateCartItem, removeFromCart, clearCart, getTotal };
};

// Hook utilitaire pour l'UI
export const useUI = () => {
  const sidebarOpen = useStore((s) => s.sidebarOpen);
  const toggleSidebar = useStore((s) => s.toggleSidebar);
  const setSidebarOpen = useStore((s) => s.setSidebarOpen);
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const toggleTheme = useStore((s) => s.toggleTheme);

  return { sidebarOpen, toggleSidebar, setSidebarOpen, theme, setTheme, toggleTheme };
};

export default useStore;
